<script setup>
// Content-page hero — full-bleed coloured band (rounded inset card) with an
// optional breadcrumb trail, headline, lead intro, and an optional
// category/topic + published meta row. Decorative treatment is always a
// Feature tonal illustration (jk-21) — one FEATURE_CARD_COLOURS set drives
// both the band's background and the illustration's line colours, so a
// single colour+variation pair (or 'random' for either) fully describes the
// hero's look. Hidden on mobile.
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import IllustrationSplash from '@/components/IllustrationSplash.vue'
import { FEATURE_CARD_COLOURS, FEATURE_CARD_VARIATIONS, resolveFeatureCardColour, resolveFeatureCardVariation, pickFromSeed } from '@/utils/featureCardIllustrations'

const props = defineProps({
  title:       { type: String, default: '' },
  hero:        { type: Object,  default: () => ({}) },   // { headline, intro, category, topic, published }
  breadcrumbs: { type: Array,  default: () => [] },       // [{ label, to? }]
  colour:      { type: String, default: 'random', validator: (v) => v === 'random' || FEATURE_CARD_COLOURS[v] !== undefined },
  variation:   { type: String, default: 'random', validator: (v) => v === 'random' || FEATURE_CARD_VARIATIONS.includes(v) },
})

// Seeded from the route path so the 'random' pick agrees between vite-ssg's
// server prerender and the client's post-hydration render - unseeded
// Math.random() re-rolls on hydration and visibly flashes to a different
// colour right after the page loads (confirmed live: a page baked purple in
// the static HTML, then flipped to green in the browser). ':variation'
// salts the second pick apart from the first so they don't move together.
//
// Resolved here (not left to IllustrationSplash) because the *same* colour
// must paint both the band's background and the illustration's lines - the
// concrete resolved key is passed down below, never 'random' at that layer,
// so there's no risk of two independent picks producing a mismatched
// band/tile pair.
// The seed must be the *normalised* path, not `fullPath`. vite-ssg prerenders
// this route as '/about/our-people' but writes it to 'about/our-people/
// index.html', so the browser's own URL is usually '/about/our-people/' — a
// different string, a different hash, and the flash comes straight back
// (measured: baked orange, hydrated green). Stripping the trailing slash
// makes both spellings agree. Dropping the query/hash (which `fullPath`
// carries and `path` doesn't) does the same job for '?utm_source=…' links,
// which are never part of the prerendered path either.
const route = useRoute()
const seed = computed(() => route.path.replace(/\/+$/, '') || '/')
const resolvedColour = computed(() => resolveFeatureCardColour(props.colour, seed.value))
const resolvedVariation = computed(() => resolveFeatureCardVariation(props.variation, `${seed.value}:variation`))
const set = computed(() => FEATURE_CARD_COLOURS[resolvedColour.value] || FEATURE_CARD_COLOURS.pink)

// Between $bp-md and $bp-hero-illustration (1300px) there isn't enough room
// for the full-height corner tile to read well, so a smaller top-right
// "splash" is used instead — only variations C/E have a safe edge pair
// (top+right) that lands correctly (bottom+left, facing the copy) once
// rotated 180°, so this picks from just those two rather than the full
// A-E set. Salted separately from the main variation pick so the two don't
// move in lockstep.
const midVariation = computed(() => pickFromSeed(['C', 'E'], `${seed.value}:mid-variation`))
</script>

<template>
  <section class="content-hero">
    <div class="band" :class="resolvedColour" :style="{ background: set.background }">
      <!-- $bp-md to $bp-hero-illustration (1300px): smaller top-right splash,
           rotated 180° so C/E's native top+right safe edges face the copy
           (bottom+left). No contentSide here — that mechanism only flips one
           axis at a time, not the diagonal a rotation gives. -->
      <IllustrationSplash
        family="featureTonal"
        :variation="midVariation"
        :colour="resolvedColour"
        class="illustration-mid"
      />

      <!-- $bp-hero-illustration (1300px) and up: today's full-height corner tile. -->
      <IllustrationSplash
        family="featureTonal"
        :variation="resolvedVariation"
        :colour="resolvedColour"
        contentSide="left"
        class="illustration-full"
      />

      <Breadcrumbs v-if="breadcrumbs.length" class="crumbs" :items="breadcrumbs" on-band />

      <h1 class="headline">{{ hero.headline || title }}</h1>
      <p v-if="hero.intro" class="intro">{{ hero.intro }}</p>

      <div v-if="hero.category || hero.topic || hero.published" class="meta">
        <div class="tags">
          <span v-if="hero.category" class="tag category">{{ hero.category }}</span>
          <span v-if="hero.topic" class="topic">{{ hero.topic }}</span>
        </div>
        <span v-if="hero.published" class="published">{{ hero.published }}</span>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.content-hero {
  padding-top: $space-8;
  padding-inline: $gutter;
  // Margin, not padding, on the trailing edge — lets it collapse with
  // whatever follows' own top spacing instead of the two summing (a page
  // with its own $space-8 top padding used to stack to 128px total; margin
  // collapse takes the larger of the two, 64px, instead).
  margin-bottom: $space-8;
  background: var(--color-bg);

  .band {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    max-width: $container;
    margin-inline: auto;
    padding: $space-6;
    background: var(--color-hero-band);
    border-radius: $radius-lg;
    color: var(--color-ink);
    display: flex;
    flex-direction: column;
    gap: $space-5;

    // min-height, not a fixed height or max-height alone — a ceiling with no
    // floor let short-content pages shrink the band well below 300px,
    // cropping most of the illustration away since it fills height:100% of
    // whatever the band ends up being; a *fixed* height risked clipping real
    // content on long-copy pages instead. min-height guarantees the
    // illustration always gets at least its full 300px, while still letting
    // the band grow taller for content that genuinely needs it.
    @include from-md { padding: $space-8; min-height: 300px; }

    // Dark FEATURE_CARD_COLOURS backgrounds need the light on-banner text
    // pairing instead of the default ink (same rule ContentCard uses,
    // against this component's own colour-key set). `blue` was added to
    // FEATURE_CARD_COLOURS (featureCardIllustrations.js, 2026-08-07) for the
    // Governance Program hero but never added here, so that hero rendered
    // dark ink on its dark blue band while ContentCard's own `.blue` variant
    // was already correct.
    &.navy, &.purple, &.green, &.blue { color: var(--color-on-banner); }
  }

  // Feature tonal illustration (jk-21) — two art-direction states, swapped
  // by breakpoint (only one of the pair is ever `display: block` at a time):
  // a smaller top-right splash from $bp-md, and today's full-height corner
  // tile from $bp-hero-illustration (1300px) up, once there's enough room
  // for it to read well. Both override FeatureTonalSplash's own defaults
  // (aspect-ratio box + rounded corners) via normal Vue attribute
  // fallthrough — see Illustration.vue's README for how that mechanism works.
  .illustration-mid {
    display: none;
    position: absolute;
    right: 0;
    top: 0;
    width: 40%;
    aspect-ratio: 546 / 464;
    transform: rotate(180deg);
    z-index: 0;
    pointer-events: none;

    @include from-md { display: block; }
    @include from-hero-illustration { display: none; }
  }

  .illustration-full {
    display: none;
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 45%;
    aspect-ratio: auto;
    border-radius: 0;
    z-index: 0;
    pointer-events: none;

    @include from-hero-illustration { display: block; }
  }

  // Copy sits above the illustration, and stays clear of both art states'
  // width (45% max) at every from-md+ size. (Breadcrumb styling now lives in
  // Breadcrumbs.vue; the .crumbs class is passed through to its root for
  // this z-index layering.)
  .crumbs, .headline, .intro, .meta {
    position: relative;
    z-index: 1;
    @include from-md { max-width: 60%; }
  }

  .headline {
    margin: 0;
    font-size: $font-size-h2;
    line-height: $lh-heading;
    letter-spacing: $ls-heading;
    font-weight: 500;
    @include from-md { font-size: $font-size-h1; font-weight: 700; }
  }

  .intro {
    margin: 0;
    // max-width comes from the shared .crumbs/.headline/.intro/.meta rule
    // above (60% from $bp-md) — no separate cap here any more.
    font-size: $font-size-lg;
    line-height: $lh-base;
  }

  .meta {
    margin-top: auto;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: $space-4;
    padding-top: $space-5;
    font-size: $font-size-sm;

    .tags {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: $space-4;
    }

    // Category pill — light paper fill, regardless of band colour.
    .tag.category {
      background: var(--color-bg);
      color: var(--color-ink);
      padding: $space-1 $space-3;
      border-radius: $radius-pill;
      font-size: $font-size-sm;
      text-transform: none;
    }
    .topic     { color: inherit; }
    .published { color: inherit; }
  }
}
</style>
